import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { OWNER_TIMEZONE, formatPrice, isBookableOfferKind, type Locale, type Offer, type Slot } from '@diana/shared'
import { api, ApiError, type ContactSettings } from '../../lib/api.js'
import { whatsappHref } from '../../lib/contact.js'
import {
  LOCALE_TAGS,
  addDaysIso,
  browserTimezone,
  dateIn,
  isoDateIn,
  monthGrid,
  timeIn,
  wallClockIn,
  weekDays,
  weekTitle,
} from '../../lib/datetime.js'
import { useContent } from '../../hooks/useContent.js'
import { useLocale, usePath } from '../../i18n/index.js'
import { ViewToggle, WeekGrid, type WeekEvent } from '../../components/WeekGrid.js'

/**
 * Book a lesson.
 *
 * Two calendars, the same as Diana sees in her panel: a month to find a day
 * with free time, a week to pick the hour. The visitor only ever sees FREE
 * slots — nothing about who holds the others reaches the browser.
 *
 * What they pick decides what happens:
 *   · the free intro call books the slot on the spot
 *   · a lesson or a package sends a REQUEST — Diana gets in touch to agree
 *     the details and, for packages, a regular weekly time
 *
 * A cennik card links here with `?oferta=<slug>`, which pre-selects that
 * offer. After a booking the visitor is sent on to Diana's Telegram, if she
 * has put the link in settings — that is where the details get agreed.
 *
 * Every time on this page is rendered in the VISITOR's timezone and labelled
 * as such. A student in Kyiv who sees an unlabelled "16:00" turns up an hour
 * wrong exactly once, and then doesn't come back.
 */

/** How long the confirmation stays on screen before Telegram opens. */
const TELEGRAM_REDIRECT_SECONDS = 5

type PageStrings = {
  doneTitle: string
  doneConfirmedBefore: string
  doneConfirmedAfter: string
  doneTimezone: (tz: string) => string
  doneFollowUp: string
  requestedTitle: string
  requestedBody: string
  requestedSlot: string
  eyebrow: ReactNode
  title: string
  intro: string
  timezoneNote: (tz: string) => string
  teachingFrom: (tz: string) => string
  loading: string
  emptyTitle: string
  emptyBody: (contactHref: string) => ReactNode
  viewMonth: string
  viewWeek: string
  today: string
  freeCount: (n: number) => string
  pickDayHint: string
  noSlotsWeek: string
  yourSlot: string
  offerLabel: string
  free: string
  packageNote: string
  nameLabel: string
  emailLabel: string
  phoneLabel: string
  levelLabel: string
  levelPlaceholder: string
  goalsLabel: string
  goalsPlaceholder: string
  errorTaken: string
  errorGeneric: string
  submitting: string
  submit: string
  submitRequest: string
  telegramNote: string
  telegramCountdown: (seconds: number) => string
  telegramButton: string
  close: string
  writeTitle: string
  writeBody: string
  telegramChat: string
  whatsappChat: string
  chatPrefill: (slot: string, offer: string) => string
}

const T: Record<Locale, PageStrings> = {
  pl: {
    doneTitle: 'Do zobaczenia!',
    doneConfirmedBefore: 'Rezerwacja potwierdzona na ',
    doneConfirmedAfter: '.',
    doneTimezone: (tz) => `Czas w Twojej strefie (${tz}).`,
    doneFollowUp: 'Napiszę do Ciebie z linkiem do spotkania.',
    requestedTitle: 'Dziękuję!',
    requestedBody:
      'Mam Twoje zgłoszenie. Wkrótce napiszę, żeby ustalić z Tobą wolne terminy i stały, cotygodniowy termin lekcji.',
    requestedSlot: 'Wybrany termin: ',
    eyebrow: (
      <>
        Poznajmy się — <span className="text-gold-600">za darmo</span>
      </>
    ),
    title: 'Wybierz termin',
    intro:
      'Rozmowa próbna trwa 30 minut i nic nie kosztuje. Możesz też od razu wybrać lekcję albo pakiet — wtedy odezwę się, żeby ustalić szczegóły.',
    timezoneNote: (tz) => `Godziny w Twojej strefie czasowej: ${tz}`,
    teachingFrom: (tz) => `uczę z ${tz}`,
    loading: 'Sprawdzam wolne terminy…',
    emptyTitle: 'Nie mam teraz wolnych terminów.',
    emptyBody: (contactHref) => (
      <>
        Przejrzyj kolejne tygodnie strzałkami albo napisz do mnie przez{' '}
        <a href={contactHref} className="text-gold-600 underline">
          formularz kontaktowy
        </a>{' '}
        — odezwę się, gdy zwolni się miejsce.
      </>
    ),
    viewMonth: 'Miesiąc',
    viewWeek: 'Tydzień',
    today: 'Dziś',
    freeCount: (n) => `wolne: ${n}`,
    pickDayHint: 'Kliknij dzień, żeby zobaczyć godziny.',
    noSlotsWeek: 'W tym tygodniu nie ma wolnych terminów.',
    yourSlot: 'Twój termin',
    offerLabel: 'Co wybierasz?',
    free: 'Gratis',
    packageNote:
      'Po wysłaniu odezwę się do Ciebie, żeby ustalić wolne terminy i stały, cotygodniowy termin lekcji.',
    nameLabel: 'Imię',
    emailLabel: 'Adres e-mail',
    phoneLabel: 'Telefon (opcjonalnie)',
    levelLabel: 'Twój poziom (jeśli wiesz)',
    levelPlaceholder: 'np. A2, początkujący',
    goalsLabel: 'Po co uczysz się polskiego?',
    goalsPlaceholder: 'Praca, rodzina, egzamin, codzienne życie…',
    errorTaken: 'Ten termin właśnie ktoś zajął. Wybierz inny — lista jest już odświeżona.',
    errorGeneric: 'Nie udało się zarezerwować. Spróbuj jeszcze raz.',
    submitting: 'Wysyłam…',
    submit: 'Rezerwuję ten termin',
    submitRequest: 'Wyślij zgłoszenie',
    telegramNote:
      'Wybierz dogodny termin, a po rezerwacji przejdziesz do Telegrama, gdzie ustalimy dalsze szczegóły.',
    telegramCountdown: (n) => `Za ${n} s przejdziesz do Telegrama…`,
    telegramButton: 'Przejdź do Telegrama teraz',
    close: 'Zamknij',
    writeTitle: 'Wolisz najpierw napisać?',
    writeBody: 'Napisz do mnie prosto z czatu — odpowiadam osobiście.',
    telegramChat: 'Napisz na Telegramie',
    whatsappChat: 'Napisz na WhatsAppie',
    chatPrefill: (slot, offer) =>
      `Cześć! Piszę w sprawie lekcji polskiego. Wybrany termin: ${slot}${offer ? ` · ${offer}` : ''}.`,
  },
  uk: {
    doneTitle: 'До зустрічі!',
    doneConfirmedBefore: 'Зустріч заброньовано: ',
    doneConfirmedAfter: '.',
    doneTimezone: (tz) => `Час у твоєму часовому поясі (${tz}).`,
    doneFollowUp: 'Напишу тобі й надішлю посилання на зустріч.',
    requestedTitle: 'Дякую!',
    requestedBody:
      'Твою заявку отримано. Незабаром напишу, щоб узгодити вільні години та постійний щотижневий час занять.',
    requestedSlot: 'Обраний час: ',
    eyebrow: (
      <>
        Познайомимося — <span className="text-gold-600">безкоштовно</span>
      </>
    ),
    title: 'Обери час',
    intro:
      'Пробна розмова триває 30 хвилин і нічого не коштує. Можеш одразу обрати урок або пакет — тоді я напишу, щоб узгодити деталі.',
    timezoneNote: (tz) => `Час у твоєму часовому поясі: ${tz}`,
    teachingFrom: (tz) => `уроки веду з ${tz}`,
    loading: 'Шукаю вільні години…',
    emptyTitle: 'Зараз у мене немає вільних місць.',
    emptyBody: (contactHref) => (
      <>
        Переглянь наступні тижні стрілками або напиши мені через{' '}
        <a href={contactHref} className="text-gold-600 underline">
          контактну форму
        </a>{' '}
        — озвуся, щойно звільниться місце.
      </>
    ),
    viewMonth: 'Місяць',
    viewWeek: 'Тиждень',
    today: 'Сьогодні',
    freeCount: (n) => `вільно: ${n}`,
    pickDayHint: 'Натисни на день, щоб побачити години.',
    noSlotsWeek: 'Цього тижня вільних годин немає.',
    yourSlot: 'Твій час',
    offerLabel: 'Що обираєш?',
    free: 'Безкоштовно',
    packageNote:
      'Після надсилання я напишу тобі, щоб узгодити вільні години та постійний щотижневий час занять.',
    nameLabel: 'Ім’я',
    emailLabel: 'Електронна пошта',
    phoneLabel: 'Телефон (необов’язково)',
    levelLabel: 'Твій рівень (якщо знаєш)',
    levelPlaceholder: 'напр. A2, початківець',
    goalsLabel: 'Навіщо вчиш польську?',
    goalsPlaceholder: 'Робота, сім’я, іспит, повсякденне життя…',
    errorTaken: 'Цей час щойно хтось зайняв. Обери інший — список уже оновлено.',
    errorGeneric: 'Не вдалося забронювати. Спробуй ще раз.',
    submitting: 'Надсилаю…',
    submit: 'Бронюю цей час',
    submitRequest: 'Надіслати заявку',
    telegramNote:
      'Обери зручний час — після бронювання перейдеш у Telegram, де ми узгодимо деталі.',
    telegramCountdown: (n) => `За ${n} с перейдеш у Telegram…`,
    telegramButton: 'Перейти в Telegram зараз',
    close: 'Закрити',
    writeTitle: 'Хочеш спершу написати?',
    writeBody: 'Напиши мені прямо в чат — відповідаю особисто.',
    telegramChat: 'Написати в Telegram',
    whatsappChat: 'Написати у WhatsApp',
    chatPrefill: (slot, offer) =>
      `Вітаю! Пишу щодо уроків польської. Обраний час: ${slot}${offer ? ` · ${offer}` : ''}.`,
  },
  ru: {
    doneTitle: 'До встречи!',
    doneConfirmedBefore: 'Встреча забронирована: ',
    doneConfirmedAfter: '.',
    doneTimezone: (tz) => `Время в твоём часовом поясе (${tz}).`,
    doneFollowUp: 'Напишу тебе и пришлю ссылку на встречу.',
    requestedTitle: 'Спасибо!',
    requestedBody:
      'Твоя заявка получена. Скоро напишу, чтобы согласовать свободные окна и постоянное еженедельное время занятий.',
    requestedSlot: 'Выбранное время: ',
    eyebrow: (
      <>
        Давай познакомимся — <span className="text-gold-600">бесплатно</span>
      </>
    ),
    title: 'Выбери время',
    intro:
      'Пробная встреча длится 30 минут и ничего не стоит. Можно сразу выбрать урок или пакет — тогда я напишу, чтобы обсудить детали.',
    timezoneNote: (tz) => `Время в твоём часовом поясе: ${tz}`,
    teachingFrom: (tz) => `уроки веду из ${tz}`,
    loading: 'Ищу свободные окна…',
    emptyTitle: 'Сейчас у меня нет свободных окон.',
    emptyBody: (contactHref) => (
      <>
        Посмотри следующие недели стрелками или напиши мне через{' '}
        <a href={contactHref} className="text-gold-600 underline">
          контактную форму
        </a>{' '}
        — отвечу, как только появится место.
      </>
    ),
    viewMonth: 'Месяц',
    viewWeek: 'Неделя',
    today: 'Сегодня',
    freeCount: (n) => `свободно: ${n}`,
    pickDayHint: 'Нажми на день, чтобы увидеть часы.',
    noSlotsWeek: 'На этой неделе свободных окон нет.',
    yourSlot: 'Твоё время',
    offerLabel: 'Что выбираешь?',
    free: 'Бесплатно',
    packageNote:
      'После отправки я напишу тебе, чтобы согласовать свободные окна и постоянное еженедельное время занятий.',
    nameLabel: 'Имя',
    emailLabel: 'Электронная почта',
    phoneLabel: 'Телефон (необязательно)',
    levelLabel: 'Твой уровень (если знаешь)',
    levelPlaceholder: 'напр. A2, начинающий',
    goalsLabel: 'Зачем учишь польский?',
    goalsPlaceholder: 'Работа, семья, экзамен, повседневная жизнь…',
    errorTaken: 'Это время только что кто-то занял. Выбери другое — список уже обновлён.',
    errorGeneric: 'Не удалось забронировать. Попробуй ещё раз.',
    submitting: 'Отправляю…',
    submit: 'Бронирую это время',
    submitRequest: 'Отправить заявку',
    telegramNote:
      'Выбери удобное время — после бронирования перейдёшь в Telegram, где мы обсудим детали.',
    telegramCountdown: (n) => `Через ${n} с перейдёшь в Telegram…`,
    telegramButton: 'Перейти в Telegram сейчас',
    close: 'Закрыть',
    writeTitle: 'Хочешь сначала написать?',
    writeBody: 'Напиши мне прямо в чат — отвечаю лично.',
    telegramChat: 'Написать в Telegram',
    whatsappChat: 'Написать в WhatsApp',
    chatPrefill: (slot, offer) =>
      `Привет! Пишу насчёт уроков польского. Выбранное время: ${slot}${offer ? ` · ${offer}` : ''}.`,
  },
}

const HORIZON_DAYS = 60
const DAY_START = 8
const DAY_END = 20
const WEEKDAY_INITIALS: Record<Locale, string[]> = {
  pl: ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'],
  uk: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'],
  ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
}

export function BookingPage() {
  const locale = useLocale()
  const to = usePath()
  const s = T[locale]
  const tag = LOCALE_TAGS[locale] ?? 'pl-PL'

  const tz = browserTimezone()
  const today = isoDateIn(new Date(), tz)
  const horizon = addDaysIso(today, HORIZON_DAYS)

  const [view, setView] = useState<'month' | 'week'>('month')
  const [anchor, setAnchor] = useState(today)
  const [selected, setSelected] = useState<Slot | null>(null)
  const [offerId, setOfferId] = useState<string | null>(null)
  const [form, setForm] = useState({ name: '', email: '', phone: '', goals: '', level: '' })
  const [done, setDone] = useState<'booked' | 'requested' | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['slots', today, horizon],
    queryFn: () => api.booking.slots(today, horizon),
  })
  const content = useContent(locale)

  /**
   * What can be picked. The trial is the default; anything else becomes a
   * request. Offers come in the visitor's language, falling back to Polish.
   */
  const offers = useMemo(() => {
    const list = (content.data?.offers ?? []).filter((o) => o.active && isBookableOfferKind(o.kind))
    return [...list].sort((a, b) => (a.kind === 'trial' ? -1 : b.kind === 'trial' ? 1 : a.order - b.order))
  }, [content.data])
  // Until they pick something themselves: the card they clicked on the
  // cennik (`?oferta=`), else the trial. An unknown slug just means "trial".
  const [params] = useSearchParams()
  const wantedSlug = params.get('oferta')
  const chosenOffer =
    offers.find((o) => o.id === offerId) ??
    (wantedSlug ? offers.find((o) => o.slug === wantedSlug) : undefined) ??
    offers[0] ??
    null
  const isRequest = chosenOffer !== null && chosenOffer.kind !== 'trial'
  const contact = content.data?.settings.contact as ContactSettings | undefined
  const telegram = (contact?.telegram ?? '').trim()
  const whatsapp = (contact?.whatsapp ?? '').trim()

  const create = useMutation({
    mutationFn: () =>
      api.booking.create({
        startsAt: selected!.startsAt,
        offerId: chosenOffer?.id ?? null,
        name: form.name,
        email: form.email,
        phone: form.phone,
        goals: form.goals,
        level: form.level,
        timezone: tz,
        locale,
      }),
    onSuccess: (res) => setDone(res.booking.status === 'pending' ? 'requested' : 'booked'),
  })

  const slotLabel = selected ? `${dateIn(selected.startsAt, tz, tag)}, ${timeIn(selected.startsAt, tz)}` : ''

  const whatsappUrl = whatsappHref(whatsapp, s.chatPrefill(slotLabel, chosenOffer?.title ?? ''))

  function closeDialog() {
    setSelected(null)
    create.reset()
  }

  /* While the modal is up it owns the screen: Escape closes it, and the page
     behind stops scrolling away under it on a phone. */
  useEffect(() => {
    if (!selected || done) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeDialog()
    window.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [selected, done])

  /** Slots by the visitor's own calendar day. */
  const byDay = useMemo(() => {
    const out = new Map<string, Slot[]>()
    for (const slot of data?.slots ?? []) {
      const key = isoDateIn(slot.startsAt, tz)
      out.set(key, [...(out.get(key) ?? []), slot])
    }
    return out
  }, [data, tz])

  const [year, month] = anchor.split('-').map(Number) as [number, number]
  const cells = useMemo(() => monthGrid(year, month - 1, today), [year, month, today])
  const days = useMemo(() => weekDays(anchor), [anchor])

  /** The week grid stretches if Diana's hours land outside 08–20 in this zone. */
  const weekEvents = useMemo(() => {
    const events: WeekEvent[] = []
    let start = DAY_START
    let end = DAY_END
    for (const day of days) {
      for (const slot of byDay.get(day) ?? []) {
        const a = wallClockIn(slot.startsAt, tz)
        const b = wallClockIn(slot.endsAt, tz)
        const endMin = b.day === a.day ? b.minutes : 24 * 60
        start = Math.min(start, Math.floor(a.minutes / 60))
        end = Math.max(end, Math.ceil(endMin / 60))
        events.push({
          id: slot.startsAt,
          day,
          startMin: a.minutes,
          endMin,
          label: timeIn(slot.startsAt, tz),
          sub: `${slot.minutes} min`,
          tone: selected?.startsAt === slot.startsAt ? 'selected' : 'free',
          onClick: () => setSelected(slot),
        })
      }
    }
    return { events, start, end }
  }, [days, byDay, tz, selected])

  const canGoBack = view === 'month' ? anchor.slice(0, 7) > today.slice(0, 7) : days[0]! > today
  const canGoForward =
    view === 'month' ? anchor.slice(0, 7) < horizon.slice(0, 7) : days[6]! < horizon

  function shift(delta: number) {
    if (view === 'week') {
      setAnchor(addDaysIso(days[0]!, delta * 7))
    } else {
      const m = month - 1 + delta
      const y = year + Math.floor(m / 12)
      setAnchor(`${y}-${String((((m % 12) + 12) % 12) + 1).padStart(2, '0')}-01`)
    }
  }

  const monthTitle = new Intl.DateTimeFormat(tag, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${anchor.slice(0, 7)}-15T12:00:00Z`),
  )

  /* ── done screens ─────────────────────────────────────────────────── */

  if (done === 'booked' && selected) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-20 text-center">
        <h1 className="h-display text-3xl italic">{s.doneTitle}</h1>
        <p className="mt-5 text-lg font-light">
          {s.doneConfirmedBefore}
          <strong className="font-roboto font-bold">{slotLabel}</strong>
          {s.doneConfirmedAfter}
        </p>
        <p className="mt-2 font-light text-ink-faint">{s.doneTimezone(tz)}</p>
        {telegram ? (
          <TelegramRedirect url={telegram} label={s.telegramButton} countdown={s.telegramCountdown} />
        ) : (
          <p className="mt-6 font-light">{s.doneFollowUp}</p>
        )}
        {/* Telegram gets the redirect; WhatsApp is one tap away for anyone who lives there instead. */}
        {whatsappUrl && (
          <a href={whatsappUrl} target="_blank" rel="noreferrer" className="btn btn-outline mt-4">
            {s.whatsappChat}
          </a>
        )}
      </div>
    )
  }

  if (done === 'requested' && selected) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-20 text-center">
        <h1 className="h-display text-3xl italic">{s.requestedTitle}</h1>
        <p className="mt-5 text-lg font-light">{s.requestedBody}</p>
        <p className="mt-6 font-light text-ink-faint">
          {s.requestedSlot}
          <strong className="font-roboto font-bold text-ink">{slotLabel}</strong>
          {chosenOffer && ` · ${chosenOffer.title}`}
        </p>
        {telegram && <TelegramRedirect url={telegram} label={s.telegramButton} countdown={s.telegramCountdown} />}
        {whatsappUrl && (
          <a href={whatsappUrl} target="_blank" rel="noreferrer" className="btn btn-outline mt-4">
            {s.whatsappChat}
          </a>
        )}
      </div>
    )
  }

  const nothingFree = !isLoading && byDay.size === 0

  return (
    <div className="mx-auto max-w-4xl px-5 py-16">
      <h2 className="h-display text-2xl italic sm:text-3xl">{s.eyebrow}</h2>
      <h1 className="h-display mt-3 text-3xl sm:text-4xl">{s.title}</h1>
      <p className="mt-5 max-w-xl text-lg leading-7 font-light">{s.intro}</p>
      {telegram && (
        <p className="mt-5 max-w-xl rounded-sm bg-sky-100 px-5 py-4 text-[0.95rem] leading-relaxed font-light">
          {s.telegramNote}
        </p>
      )}

      <p className="mt-3 font-roboto text-xs uppercase tracking-widest text-ink-faint">
        {s.timezoneNote(tz)}
        {tz !== OWNER_TIMEZONE && ` · ${s.teachingFrom(OWNER_TIMEZONE)}`}
      </p>

      {isLoading && <p className="mt-10 font-light">{s.loading}</p>}

      {/*
        The calendar stays on screen even with nothing free in the next two
        months. Hiding it left a visitor with a flat "no times" card and no
        way to look at another week — and it made an empty booking page
        indistinguishable from a broken one.
      */}
      {nothingFree && (
        <div className="mt-10 rounded-sm bg-sky-100 px-6 py-5 text-center">
          <p className="h-display text-lg italic">{s.emptyTitle}</p>
          <p className="mt-2 text-[0.95rem] font-light">{s.emptyBody(to('/kontakt'))}</p>
        </div>
      )}

      {!isLoading && (
        <>
          {/* ── calendar chrome ─────────────────────────────────────── */}
          <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
            <ViewToggle value={view} onChange={setView} labels={{ month: s.viewMonth, week: s.viewWeek }} />
            <div className="flex items-center gap-2">
              <button onClick={() => shift(-1)} disabled={!canGoBack} aria-label="‹" className="nav-arrow disabled:opacity-30">
                ‹
              </button>
              <span className="min-w-52 text-center font-roboto text-base font-bold uppercase tracking-tight first-letter:uppercase">
                {view === 'month' ? monthTitle : weekTitle(days, tag)}
              </span>
              <button onClick={() => shift(1)} disabled={!canGoForward} aria-label="›" className="nav-arrow disabled:opacity-30">
                ›
              </button>
              <button
                onClick={() => setAnchor(today)}
                className="ml-1 rounded-full border border-sky-200 px-3 py-1.5 font-roboto text-xs font-bold uppercase tracking-widest hover:border-gold"
              >
                {s.today}
              </button>
            </div>
          </div>

          {view === 'month' ? (
            <>
              <p className="mt-3 text-sm font-light text-ink-faint">{s.pickDayHint}</p>
              <div className="mt-3 grid grid-cols-7 gap-1.5 sm:gap-2">
                {WEEKDAY_INITIALS[locale].map((d) => (
                  <div key={d} className="pb-1 text-center font-roboto text-[0.7rem] font-bold uppercase tracking-widest text-ink-faint">
                    {d}
                  </div>
                ))}
                {cells.map((cell) => {
                  const n = byDay.get(cell.iso)?.length ?? 0
                  const usable = n > 0 && cell.iso >= today
                  return (
                    <button
                      key={cell.iso}
                      type="button"
                      disabled={!usable}
                      onClick={() => {
                        setAnchor(cell.iso)
                        setView('week')
                      }}
                      className={[
                        'flex min-h-16 flex-col rounded-sm border p-2 text-left transition sm:min-h-20',
                        cell.inMonth ? '' : 'opacity-35',
                        usable ? 'border-gold bg-white hover:bg-gold-100' : 'border-sky-200 bg-white/40 text-ink-faint',
                        cell.isToday ? 'ring-2 ring-gold ring-offset-1' : '',
                      ].join(' ')}
                    >
                      <span className="font-roboto text-sm font-bold">{cell.day}</span>
                      {usable && (
                        <span className="mt-auto font-roboto text-[0.65rem] font-bold uppercase tracking-wider text-gold-600">
                          {s.freeCount(n)}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </>
          ) : (
            <div className="mt-4">
              {weekEvents.events.length === 0 && (
                <p className="mb-3 text-sm font-light text-ink-faint">{s.noSlotsWeek}</p>
              )}
              <WeekGrid
                days={days}
                startHour={weekEvents.start}
                endHour={weekEvents.end}
                locale={tag}
                today={today}
                events={weekEvents.events}
                cellClass={(day) => (day < today ? 'bg-sky-50' : '')}
                dayBadge={(day) => {
                  const n = byDay.get(day)?.length ?? 0
                  return n > 0 ? <span className="font-roboto text-[0.65rem] font-bold text-gold-600">{n}</span> : null
                }}
              />
            </div>
          )}
        </>
      )}

      {/* ── the form, in a modal ───────────────────────────────────── */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-6"
          onClick={closeDialog}
          role="dialog"
          aria-modal="true"
          aria-label={s.yourSlot}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault()
              create.mutate()
            }}
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-[1.5rem] bg-white p-7 sm:rounded-sm"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow">{s.yourSlot}</p>
                <p className="h-display mt-1 text-2xl italic text-gold-600 first-letter:uppercase">
                  {slotLabel}
                </p>
              </div>
              <button
                type="button"
                onClick={closeDialog}
                aria-label={s.close}
                className="text-2xl leading-none text-ink-faint hover:text-ink"
              >
                ×
              </button>
            </div>

            {offers.length > 0 && (
              <fieldset className="mt-6">
                <legend className="mb-2 block font-roboto text-xs font-bold uppercase tracking-widest text-ink-faint">
                  {s.offerLabel}
                </legend>
                <div className="grid gap-2">
                  {offers.map((o) => (
                    <OfferChoice
                      key={o.id}
                      offer={o}
                      checked={chosenOffer?.id === o.id}
                      onPick={() => setOfferId(o.id)}
                      freeLabel={s.free}
                    />
                  ))}
                </div>
                {isRequest && <p className="mt-3 text-sm font-light text-ink-soft">{s.packageNote}</p>}
              </fieldset>
            )}

            <div className="mt-6 space-y-4">
              <Field label={s.nameLabel} htmlFor="b-name">
                <input id="b-name" required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field" />
              </Field>

              <Field label={s.emailLabel} htmlFor="b-email">
                <input id="b-email" type="email" required maxLength={200} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field" />
              </Field>

              <Field label={s.phoneLabel} htmlFor="b-phone">
                <input id="b-phone" type="tel" maxLength={40} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="field" />
              </Field>

              <Field label={s.levelLabel} htmlFor="b-level">
                <input id="b-level" maxLength={20} placeholder={s.levelPlaceholder} value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="field" />
              </Field>

              <Field label={s.goalsLabel} htmlFor="b-goals">
                <textarea id="b-goals" rows={4} maxLength={2000} placeholder={s.goalsPlaceholder} value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} className="field" />
              </Field>
            </div>

            {create.error instanceof ApiError && (
              <p className="mt-4 text-sm text-rose">{create.error.status === 409 ? s.errorTaken : s.errorGeneric}</p>
            )}

            <button type="submit" disabled={create.isPending} className="btn btn-gold mt-6 w-full disabled:opacity-60">
              {create.isPending ? s.submitting : isRequest ? s.submitRequest : s.submit}
            </button>

            {/*
              Not everyone fills in a form. The chat buttons carry the slot
              they just clicked into the message, so a conversation that
              starts here doesn't start from "which hour was it?".
            */}
            <WriteToMe
              telegram={telegram}
              whatsapp={whatsappUrl}
              s={s}
              className="mt-7 border-t border-sky-200 pt-5"
            />
          </form>
        </div>
      )}
    </div>
  )
}

/** The two chats, shown only for the ones Diana has actually filled in. */
function WriteToMe({
  telegram,
  whatsapp,
  s,
  className,
}: {
  telegram: string
  whatsapp: string | null
  s: PageStrings
  className?: string
}) {
  if (!telegram && !whatsapp) return null

  return (
    <div className={className}>
      <p className="font-roboto text-xs font-bold uppercase tracking-widest text-ink-faint">{s.writeTitle}</p>
      <p className="mt-1 text-sm font-light text-ink-soft">{s.writeBody}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {telegram && (
          <a href={telegram} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">
            {s.telegramChat}
          </a>
        )}
        {whatsapp && (
          <a href={whatsapp} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">
            {s.whatsappChat}
          </a>
        )}
      </div>
    </div>
  )
}

function OfferChoice({
  offer,
  checked,
  onPick,
  freeLabel,
}: {
  offer: Offer
  checked: boolean
  onPick: () => void
  freeLabel: string
}) {
  return (
    <label
      className={[
        'flex cursor-pointer items-start gap-3 rounded-sm border p-3 transition',
        checked ? 'border-gold bg-gold-100/40' : 'border-sky-200 hover:border-gold',
      ].join(' ')}
    >
      <input type="radio" name="offer" checked={checked} onChange={onPick} className="mt-1 accent-gold" />
      <span className="flex-1">
        <span className="block font-roboto text-sm font-bold">{offer.title}</span>
        {offer.subtitle && <span className="block text-xs font-light text-ink-soft">{offer.subtitle}</span>}
        <span className="mt-1 block font-roboto text-xs uppercase tracking-wider text-gold-600">
          {offer.priceGrosz === 0 ? freeLabel : formatPrice(offer.priceGrosz)}
          {offer.priceNote && ` ${offer.priceNote}`}
          {offer.kind === 'package' && ` · ${offer.lessons}×`}
        </span>
      </span>
    </label>
  )
}

/**
 * The confirmation stays up long enough to read, then Telegram opens. The
 * button is for anyone who won't wait — and for a browser that blocks the
 * automatic navigation.
 */
function TelegramRedirect({
  url,
  label,
  countdown,
}: {
  url: string
  label: string
  countdown: (seconds: number) => string
}) {
  const [left, setLeft] = useState(TELEGRAM_REDIRECT_SECONDS)

  useEffect(() => {
    if (left <= 0) {
      window.location.assign(url)
      return
    }
    const timer = setTimeout(() => setLeft((n) => n - 1), 1000)
    return () => clearTimeout(timer)
  }, [left, url])

  return (
    <>
      <p className="mt-6 font-light text-ink-faint" aria-live="polite">
        {countdown(left)}
      </p>
      <a href={url} className="btn btn-gold mt-6">
        {label}
      </a>
    </>
  )
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-2 block font-roboto text-xs font-bold uppercase tracking-widest text-ink-faint">
        {label}
      </label>
      {children}
    </div>
  )
}
